import { type ComponentType, type ReactNode, useCallback, useEffect, useState } from "react";
import { match } from "ts-pattern";
import { FaEligibilityOutcome } from "@medbillai/generated-graphql-types";
import { FullscreenSpinner, YStack } from "@medbillai/ui";
import { back, createFlow, type FlowState, type FlowTransition, type OnDone, type Outcomes, type ScreenKey, type TaggedUnion, type TransitionTable, tagged, useBrowserHistorySync, useNavigableFlow } from "../../lib/flow-engine";
import { ValueProps, type ValuePropsOutcomes } from "../onboarding/ValueProps";
import { BillEducation, type BillEducationOutcomes } from "./BillEducation";
import { FaResultCatastrophic, type FaResultCatastrophicOutcomes } from "./FaResultCatastrophic";
import { FaResultStrongCase, type FaResultStrongCaseOutcomes } from "./FaResultStrongCase";
import { FinancialAssistance, type FinancialAssistanceOutcomes } from "./FinancialAssistance";
import { HouseholdSize, type HouseholdSizeOutcomes } from "./HouseholdSize";
import { Income, type IncomeOutcomes } from "./Income";
import { Insurance, type InsuranceOutcomes } from "./Insurance";
import { type IntentOutcomes, IntentScreen } from "./Intent";
import { Notifications, type NotificationsOutcomes } from "./Notifications";
import { Profile, type ProfileOutcomes } from "./Profile";
import { SaveEducation, type SaveEducationOutcomes } from "./SaveEducation";
import type { FfgaData, HouseholdData, IdentityData, IntentData, ResolvedIntentData } from "./types";
export const ONBOARDING_VERSION = "v3.0" as const;
export type OnboardingConfig = {
  ValueProps: {
    needs: {};
    outcomes: ValuePropsOutcomes;
  };
  Profile: {
    needs: {};
    outcomes: ProfileOutcomes;
  };
  Intent: {
    needs: IdentityData;
    outcomes: IntentOutcomes;
  };
  BillEducation: {
    needs: IntentData & IdentityData;
    outcomes: BillEducationOutcomes;
  };
  SaveEducation: {
    needs: IntentData & IdentityData;
    outcomes: SaveEducationOutcomes;
  };
  FinancialAssistance: {
    needs: IntentData & IdentityData;
    outcomes: FinancialAssistanceOutcomes;
  };
  HouseholdSize: {
    needs: IntentData & IdentityData;
    outcomes: HouseholdSizeOutcomes;
  };
  Income: {
    needs: HouseholdData & IntentData & IdentityData;
    outcomes: IncomeOutcomes;
  };
  FaResultStrongCase: {
    needs: IntentData & IdentityData;
    outcomes: FaResultStrongCaseOutcomes;
  };
  FaResultCatastrophic: {
    needs: IntentData & IdentityData;
    outcomes: FaResultCatastrophicOutcomes;
  };
  Insurance: {
    needs: ResolvedIntentData & IdentityData;
    outcomes: InsuranceOutcomes;
  };
  Notifications: {
    needs: ResolvedIntentData;
    outcomes: NotificationsOutcomes;
  };
  Complete: {
    needs: {};
    outcomes: never;
  };
};
export type OnboardingContext = {
  isWeb: boolean;
  /**
   * General-user entry skips the FA pre-screener: the save branch goes from
   * its education beat straight to Insurance. Partner (FFGA) entry keeps it.
   * Routing is the only difference between the two entries of this flow.
   */
  skipFaFlow: boolean;
  /** Present when the user entered via an FFGA invite link (resolved token). */
  ffga?: FfgaData;
};
export const Screen = createFlow<OnboardingConfig>();

// Auth-handoff outcomes return the same screen (Noop) so the shell can
// intercept and redirect without breaking the URL-transition animation.
const transitions: TransitionTable<OnboardingConfig, OnboardingContext> = {
  ValueProps: {
    logIn: () => Screen.ValueProps(),
    signUp: () => Screen.ValueProps(),
    back: () => back()
  },
  Profile: {
    submitted: identity => Screen.Intent({
      identity
    })
  },
  Intent: {
    selected: ({
      intent
    }) => intent === "bill" ? Screen.BillEducation({
      intent
    }) : Screen.SaveEducation({
      intent
    }),
    back: () => back()
  },
  BillEducation: {
    continue: () => Screen.Insurance(),
    back: () => back()
  },
  SaveEducation: {
    continue: (_, {
      skipFaFlow
    }) => skipFaFlow ? Screen.Insurance() : Screen.FinancialAssistance(),
    back: () => back()
  },
  FinancialAssistance: {
    continue: () => Screen.HouseholdSize(),
    back: () => back()
  },
  HouseholdSize: {
    selected: ({
      householdSize
    }) => Screen.Income({
      householdSize
    }),
    back: () => back()
  },
  Income: {
    checked: ({
      outcome
    }) => match(outcome).with(FaEligibilityOutcome.STRONG_CASE, () => Screen.FaResultStrongCase()).with(FaEligibilityOutcome.CATASTROPHIC_COVERED, () => Screen.FaResultCatastrophic()).exhaustive(),
    back: () => back()
  },
  FaResultStrongCase: {
    continue: () => Screen.Insurance()
  },
  FaResultCatastrophic: {
    continue: () => Screen.Insurance()
  },
  Insurance: {
    // Carrier already persisted on-screen; native shows the push-permission
    // tail, web completes.
    selected: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications(),
    uninsured: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications(),
    skipped: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications()
  },
  Notifications: {
    configured: () => Screen.Complete(),
    skipped: () => Screen.Complete()
  },
  Complete: {}
};
type AuthHandoffShapes = {
  exitToSignup: {};
  exitToLogin: {};
};
export const AuthHandoff = tagged<AuthHandoffShapes>();
export type AuthHandoff = TaggedUnion<AuthHandoffShapes>;
export type OnboardingTransitionProps = {
  stateKey: string;
  direction: 1 | -1;
  children: ReactNode;
};
export type OnboardingFlowV3Props = {
  initial: FlowState<OnboardingConfig>;
  ctx: OnboardingContext;
  onComplete: () => void;
  onAuthHandoff: OnDone<AuthHandoffShapes>;
  onTransition?: (event: FlowTransition<OnboardingConfig>) => void;
  /** Fires with the committed state on first render and after every
   * transition — a render cycle after `onTransition`. */
  onStateLoaded?: (state: FlowState<OnboardingConfig>) => void;
  /**
   * Screen-transition wrapper — supplied per-platform by the call site so
   * each platform uses the right animation tech (framer-motion on web,
   * Tamagui+Reanimated on native).
   */
  Transition: ComponentType<OnboardingTransitionProps>;
};
export function OnboardingFlowV3({
  initial,
  ctx,
  onAuthHandoff,
  onComplete,
  onTransition: parentOnTransition,
  onStateLoaded,
  Transition
}: OnboardingFlowV3Props) {
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => {
    // Observability sees every outcome, including the auth-handoff ones the
    // match below routes out of the flow.
    parentOnTransition?.(event);
    match([event.from, event.outcome]).with([{
      tag: "ValueProps"
    }, {
      tag: "logIn"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToLogin());
    }).with([{
      tag: "ValueProps"
    }, {
      tag: "signUp"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToSignup());
    }).otherwise(() => {});
  }, [parentOnTransition, onAuthHandoff]);
  const [direction, setDirection] = useState<1 | -1>(1);
  const {
    state,
    bindOnDone: engineBindOnDone,
    canGoBack,
    goBack: engineGoBack
  } = useNavigableFlow<OnboardingConfig, OnboardingContext>({
    transitions,
    initial,
    ctx,
    onTransition,
    onStateLoaded
  });
  useEffect(() => {
    if (state.tag === "Complete") {
      onComplete();
    }
  }, [state.tag, onComplete]);

  // setState in this handler flushes synchronously when the handler returns,
  // re-rendering the outgoing screen with the new direction's `exit` prop.
  // The rAF then fires the engine dispatch in a separate batch, so when
  // AnimatePresence captures the exiting child's `exit`, direction has
  // already settled.
  const bindOnDone = <K extends ScreenKey<OnboardingConfig>,>(tag: K) => (outcome: TaggedUnion<Outcomes<OnboardingConfig, K>>) => {
    setDirection(outcome.tag === "back" ? -1 : 1);
    requestAnimationFrame(() => {
      engineBindOnDone(tag)(outcome);
    });
  };
  const goBack = () => {
    setDirection(-1);
    requestAnimationFrame(() => {
      engineGoBack();
    });
  };
  useBrowserHistorySync({
    stateTag: state.tag,
    canGoBack,
    goBack
  });
  const screen = match(state).with({
    tag: "ValueProps"
  }, () => <ValueProps onDone={bindOnDone("ValueProps")} />).with({
    tag: "Profile"
  }, () => <Profile onDone={bindOnDone("Profile")} />).with({
    tag: "Intent"
  }, () => <IntentScreen onDone={bindOnDone("Intent")} />).with({
    tag: "BillEducation"
  }, () => <BillEducation onDone={bindOnDone("BillEducation")} />).with({
    tag: "SaveEducation"
  }, () => <SaveEducation onDone={bindOnDone("SaveEducation")} />).with({
    tag: "FinancialAssistance"
  }, () => <FinancialAssistance onDone={bindOnDone("FinancialAssistance")} />).with({
    tag: "HouseholdSize"
  }, () => <HouseholdSize onDone={bindOnDone("HouseholdSize")} />).with({
    tag: "Income"
  }, ({
    data
  }) => <Income householdSize={data.householdSize} onDone={bindOnDone("Income")} />).with({
    tag: "FaResultStrongCase"
  }, () => <FaResultStrongCase onDone={bindOnDone("FaResultStrongCase")} ffga={ctx.ffga} />).with({
    tag: "FaResultCatastrophic"
  }, () => <FaResultCatastrophic onDone={bindOnDone("FaResultCatastrophic")} />).with({
    tag: "Insurance"
  }, ({
    data
  }) => <Insurance identity={data.identity} onDone={bindOnDone("Insurance")} />).with({
    tag: "Notifications"
  }, ({
    data
  }) => <Notifications intent={data.intent} onDone={bindOnDone("Notifications")} />).with({
    tag: "Complete"
  }, () => <FullscreenSpinner />).exhaustive();
  return (
    // flexBasis auto (web): in the phone page-scroll layout this wrapper
    // must size from its content so the pinned screen's 100dvh floor can
    // grow the page; `flex: 1`'s basis 0 would collapse it. In definite
    // parents (desktop shell, native) grow still fills as before.
    <YStack flex={1} position="relative" overflow="hidden" $platform-web={{
      flexBasis: "auto"
    }} data-sentry-element="YStack" data-sentry-component="OnboardingFlowV3" data-sentry-source-file="OnboardingFlowV3.tsx">
      <Transition stateKey={state.tag} direction={direction} data-sentry-element="Transition" data-sentry-source-file="OnboardingFlowV3.tsx">
        {screen}
      </Transition>
    </YStack>
  );
}